'use client';

import * as React from 'react';
import { Command as CommandPrimitive } from 'cmdk';
import { cn } from '@/lib/utils';
import { MagnifyingGlassSL } from '@tac/icons';

function Command({ className, ...props }: React.ComponentProps<typeof CommandPrimitive>) {
  return (
    <CommandPrimitive
      data-slot="command"
      className={cn(
        'flex h-full w-full flex-col bg-popover text-popover-foreground overflow-hidden within:border-greyscal-1000 focus-within:greyscale-1000',
        className
      )}
      {...props}
    />
  );
}

function CommandInput({
  className,
  touched,
  ...props
}: React.ComponentProps<typeof CommandPrimitive.Input> & { touched?: boolean }) {
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Prevent default form submission behavior
    if (e.key === 'Enter') {
      // Let cmdk handle the Enter key for selection, but prevent form submission
      const target = e.target as HTMLInputElement;
      const form = target.form;
      if (form) {
        e.preventDefault();
      }
    }
  };

  return (
    <div
      data-slot="command-input-wrapper"
      className={cn(
        'flex items-center gap-3 h-14 px-6 text-greyscale-700 rounded-lg border border-greyscale-300 outline-none transition-colors',
        'ring-1 ring-inset ring-transparent',
        'hover:border-greyscale-1000 hover:ring-greyscale-1000 focus-within:border-greyscale-1000 focus-within:ring-greyscale-1000',
        'has-[input[aria-invalid=true]]:border-destructive has-[input[aria-invalid=true]]:ring-destructive',
        'has-[input:disabled]:opacity-40'
      )}
    >
      <MagnifyingGlassSL name="search" className="size-5 shrink-0" />
      <CommandPrimitive.Input
        data-slot="command-input"
        className={cn(
          'flex w-full disabled:cursor-not-allowed outline-hidden text-base placeholder:text-greyscale-700',
          touched && 'placeholder:text-greyscale-700',
          className
        )}
        onKeyDown={handleKeyDown}
        {...props}
      />
    </div>
  );
}

function CommandList({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.List>) {
  return (
    <CommandPrimitive.List
      data-slot="command-list"
      className={cn('max-h-[300px] overflow-x-hidden overflow-y-auto scroll-py-1', className)}
      {...props}
    />
  );
}

function CommandEmpty({ ...props }: React.ComponentProps<typeof CommandPrimitive.Empty>) {
  return <CommandPrimitive.Empty data-slot="command-empty" className={cn('py-6 text-center')} {...props} />;
}

function CommandGroup({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Group>) {
  return (
    <CommandPrimitive.Group
      data-slot="command-group"
      className={cn(
        'text-foreground overflow-hidden p-2 [&_[cmdk-group-heading]]:text-muted-foreground [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5 [&_[cmdk-group-heading]]:text-xs [&_[cmdk-group-heading]]:font-medium',
        className
      )}
      {...props}
    />
  );
}

function CommandSeparator({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Separator>) {
  return (
    <CommandPrimitive.Separator
      data-slot="command-separator"
      className={cn('bg-border -mx-1 h-px', className)}
      {...props}
    />
  );
}

function CommandItem({ className, ...props }: React.ComponentProps<typeof CommandPrimitive.Item>) {
  return (
    <CommandPrimitive.Item
      data-slot="command-item"
      className={cn(
        "relative flex items-center gap-3 text-base px-4 py-3 rounded-lg hover:bg-greyscale-100 data-[selected=true]:bg-greyscale-100 data-[disabled=true]:pointer-events-none data-[disabled=true]:opacity-40 cursor-default select-none outline-hidden [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    />
  );
}

function CommandShortcut({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="command-shortcut"
      className={cn('text-xs text-muted-foreground tracking-widest ml-auto', className)}
      {...props}
    />
  );
}

export {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandShortcut,
  CommandSeparator,
};
